/**
 * resolveCompat — distill a `ResolvedParseOptions` snapshot into the version
 * facts the decoration / lint passes need.
 *
 * For MSSQL both the instance version AND the per-database compatibility
 * level matter: the more-restrictive of the two wins per feature.
 *   - Instance gates added-features (e.g. STRING_AGG requires server ≥ 2017).
 *   - Compat gates removed-features (e.g. `*=` outer join is removed at compat 90).
 * Today `ParseOptions` carries `serverVersion` but not `mssqlCompatLevel`; the
 * grammar PRD § 2.5 plans to add it. Until then, the orchestrator threads it
 * via the explicit `mssqlCompatLevel` argument. In production that value is
 * sourced from the projected schema-bus snapshot (the MSSQL cache reads
 * `sys.databases.compatibility_level` from the live connection — INT-1); the
 * E2E test override still takes precedence for mock-engine connections. This
 * module is forward-compatible: when SqlGrammar lands the field on
 * `ParseOptions`, the orchestrator can drop the explicit threading.
 */

import type {
  ResolvedParseOptions,
  SqlEngine,
} from '@/features/sql-grammar/parseOptions';

import type { VersionMetadataEntry } from './versionMetadata';

export type CompatContext = {
  readonly engineId: SqlEngine;
  /** Raw server version string from the resolved options. Undefined when the connection is unbound. */
  readonly serverVersion?: string;
  /** Parsed major version for comparison-friendly access. */
  readonly majorVersion?: number;
  readonly minorVersion?: number;
  readonly patchVersion?: number;
  /** MSSQL only: per-database compatibility level (e.g. 90, 100, 130, 150, 160). */
  readonly mssqlCompatLevel?: number;
  /** Mirrors `ParseOptions.mysqlVersionGate` when set; lets the catalog gate features that key on this enum. */
  readonly mysqlVersionGate?: 'lt80' | 'ge80';
  readonly oracleCompatLevel?: string;
};

export type ResolveCompatInput = {
  readonly resolved: ResolvedParseOptions;
  readonly mssqlCompatLevel?: number;
  /** Offset to read directives at; defaults to 0 (document start). */
  readonly atOffset?: number;
};

export function resolveCompat(input: ResolveCompatInput): CompatContext {
  const opts = input.resolved.optionsAt(input.atOffset ?? 0);
  const parsed = parseVersion(opts.serverVersion);
  return {
    engineId: opts.engine,
    serverVersion: opts.serverVersion,
    majorVersion: parsed?.major,
    minorVersion: parsed?.minor,
    patchVersion: parsed?.patch,
    mssqlCompatLevel: input.mssqlCompatLevel,
    mysqlVersionGate: opts.mysqlVersionGate,
    oracleCompatLevel: opts.oracleCompatLevel,
  };
}

export type FeatureStatus = 'supported' | 'unavailable' | 'deprecated';

/**
 * Cross-checks an entry against the resolved compat. Returns `'unavailable'`
 * if the feature was added in a newer version than the server reports;
 * `'deprecated'` when the MSSQL compat level reaches deprecation; falls back
 * to `'supported'`. When the server version is unknown (unbound connection
 * or pasted-SQL flow), every feature is treated as supported — annotating
 * unbound is hostile UX.
 */
export function classifyFeature(
  entry: VersionMetadataEntry,
  compat: CompatContext,
): FeatureStatus {
  if (compat.engineId !== entry.engineId) return 'supported';

  // MSSQL compat-level gates take precedence when present. PRD UX buckets
  // both "removed at compat" and "deprecated at compat" into the 'deprecated'
  // status — both render with strikethrough + detail copy + tier-1 demote.
  // The narrower distinction lives in `entry.removedAtCompat` vs
  // `entry.deprecatedAtCompat` for downstream callers that need it (e.g. the
  // detail-line copy in `decorate.ts`).
  if (entry.engineId === 'mssql' && compat.mssqlCompatLevel !== undefined) {
    if (
      entry.removedAtCompat !== undefined &&
      compat.mssqlCompatLevel >= entry.removedAtCompat
    ) {
      return 'deprecated';
    }
    if (
      entry.deprecatedAtCompat !== undefined &&
      compat.mssqlCompatLevel >= entry.deprecatedAtCompat
    ) {
      return 'deprecated';
    }
  }

  if (entry.addedIn !== undefined && compat.serverVersion !== undefined) {
    if (compareVersions(compat.serverVersion, entry.addedIn) < 0) {
      return 'unavailable';
    }
  }

  return 'supported';
}

/** Public version-comparison helper exposed for callers that want it. */
export function compareVersions(a: string, b: string): number {
  const pa = parseVersionParts(a);
  const pb = parseVersionParts(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const ai = pa[i] ?? 0;
    const bi = pb[i] ?? 0;
    if (ai !== bi) return ai - bi;
  }
  return 0;
}

function parseVersion(
  v: string | undefined,
): { major: number; minor: number; patch: number } | undefined {
  if (!v) return undefined;
  const parts = parseVersionParts(v);
  return {
    major: parts[0] ?? 0,
    minor: parts[1] ?? 0,
    patch: parts[2] ?? 0,
  };
}

function parseVersionParts(v: string): number[] {
  // Tolerates bare-major ('15'), dotted ('8.0.16'), suffixed ('12c', '11g R2'),
  // and Microsoft year format ('2017').
  return v
    .split(/[^\d]+/)
    .filter((x) => x.length > 0)
    .map((x) => Number(x))
    .filter((n) => !Number.isNaN(n));
}
